import { useEffect, useState } from "react";
import client from "../../../../api/client";
import { Avatar, Bar, Pill, STATUS } from "./staffUi";

const CAP = { passed: ["ok", "Passed"], submitted: ["brand", "Submitted"], redo_required: ["risk", "Redo required"], not_submitted: ["gray", "Not submitted"], left_out: ["watch", "Left out by team"] };
const ago = (n) => (n == null ? "No activity yet" : n === 0 ? "Today" : n === 1 ? "Yesterday" : `${n} days ago`);
const pctOf = (n, d) => (d ? Math.round((n / d) * 100) : 0);

/**
 * Every course in a cohort side by side, for decisions across the cohort: which courses need
 * attention, and every student with their course. Picking a row opens that course's detail.
 */
export default function CohortProgress({ cohortId, onOpenCourse }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    setData(null); setError(false);
    client.get("/staff/progress/cohort", { params: { cohort_id: cohortId } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [cohortId]);

  if (error) return <section className="complete-box"><div><strong>We couldn't load this cohort</strong><small>Check your connection and try again.</small></div></section>;
  if (!data) return <><div className="skeleton" style={{ height: 110 }} /><div className="skeleton" style={{ height: 320 }} /></>;

  const t = data.totals;
  const rows = data.students.filter((s) => (filter === "all" || s.status === filter) && `${s.name} ${s.email} ${s.course}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => ({ risk: 0, watch: 1, ok: 2 }[a.status] - { risk: 0, watch: 1, ok: 2 }[b.status]) || a.name.localeCompare(b.name));

  return (
    <>
      <div className="sp-mini" style={{ marginBottom: 14 }}>
        <div><b>{t.students}</b><span>Students · {data.courses.length} courses{t.enrolments > t.students ? ` · ${t.enrolments - t.students} in two` : ""}</span></div>
        <div><b>{t.attendance ?? "—"}{t.attendance != null ? "%" : ""}</b><span>Average attendance</span></div>
        <div><b>{t.activity ?? "—"}{t.activity != null ? "%" : ""}</b><span>Average activities</span></div>
        <div><b style={{ color: t.risk ? "#c0392b" : undefined }}>{t.risk}</b><span>At risk · {t.watch} to watch</span></div>
      </div>

      <section className="sp-box">
        <div className="sp-box-h"><h2>Courses in {data.cohort.name}</h2><small>Pick a course to see its students, classes and activities</small></div>
        <div className="sp-table-wrap"><table className="sp-table">
          <thead><tr><th>Course</th><th className="num">Students</th><th>Attendance</th><th>Activities</th><th>Status</th><th className="num">Inactive 7+ days</th><th className="num">Certificates</th></tr></thead>
          <tbody>
            {data.courses.map((c) => (
              <tr key={c.course_id} className="click" tabIndex={0} onClick={() => onOpenCourse(c.course_id)} onKeyDown={(e) => e.key === "Enter" && onOpenCourse(c.course_id)}>
                <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{c.course}</strong><div className="course-tag" style={{ fontSize: 12.5 }}>{c.classes} classes held</div></td>
                <td className="num">{c.students}</td>
                <td><Bar value={c.attendance} /></td>
                <td><Bar value={c.activity} /></td>
                <td>
                  <div className="sp-split" title={`${c.ok} on track · ${c.watch} watch · ${c.risk} at risk`}>
                    <i className="ok" style={{ width: `${pctOf(c.ok, c.students)}%` }} /><i className="watch" style={{ width: `${pctOf(c.watch, c.students)}%` }} /><i className="risk" style={{ width: `${pctOf(c.risk, c.students)}%` }} />
                  </div>
                  <small className="course-tag" style={{ fontSize: 12 }}>{c.ok} on track · {c.watch} watch · <span style={{ color: c.risk ? "#c0392b" : undefined }}>{c.risk} at risk</span></small>
                </td>
                <td className="num" style={c.inactive ? { color: "#c0392b", fontWeight: 500 } : undefined}>{c.inactive}</td>
                <td className="num">{c.issued} issued<div className="course-tag" style={{ fontSize: 12 }}>{c.ready - c.issued > 0 ? `${c.ready - c.issued} ready` : "—"}</div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </section>

      <section className="sp-box">
        <div className="sp-tools">
          <input type="search" placeholder="Search name, email or course" aria-label="Search students" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="lb-tabs chips-row" role="group" aria-label="Status">
            {[["all", "All", data.students.length], ["risk", "At risk", t.risk], ["watch", "Watch", t.watch], ["ok", "On track", t.ok]].map(([k, l, n]) => <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l} · {n}</button>)}
          </div>
        </div>
        <div className="sp-table-wrap"><table className="sp-table">
          <thead><tr><th>Student</th><th>Course</th><th>Status</th><th>Attendance</th><th>Activities</th><th>Capstone</th><th>Last active</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={`${s.id}-${s.course_id}`} className="click" tabIndex={0} onClick={() => onOpenCourse(s.course_id, s.id)} onKeyDown={(e) => e.key === "Enter" && onOpenCourse(s.course_id, s.id)}>
                <td><div className="sp-stu"><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.email}</small></div></div></td>
                <td className="course-tag" style={{ fontSize: 13 }}>{s.course}</td>
                <td><Pill cls={STATUS[s.status][0]}>{STATUS[s.status][1]}</Pill></td>
                <td><Bar value={s.attendance} /></td>
                <td><Bar value={s.activity} /></td>
                <td><Pill cls={CAP[s.capstone][0]}>{CAP[s.capstone][1]}</Pill></td>
                <td style={s.idle_days >= 7 ? { color: "#c0392b", fontWeight: 500 } : { color: "var(--muted)" }}>{ago(s.idle_days)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="course-tag">No students match.</td></tr>}
          </tbody>
        </table></div>
      </section>
    </>
  );
}
